import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const SUPPORTED_FORMATS = [
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
];

type DateTimeInput = string | number | Date | Dayjs | null | undefined;

interface ConvertDateTimeParams {
  dateTime: DateTimeInput;
  platformTime: DateTimeInput;
}

export const parseDateTime = (value: DateTimeInput): Dayjs | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date || typeof value === 'number') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof value === 'string') {
    const strictParsed = dayjs(value, SUPPORTED_FORMATS, true);
    if (strictParsed.isValid()) {
      return strictParsed;
    }

    const fallbackParsed = dayjs(value);
    return fallbackParsed.isValid() ? fallbackParsed : null;
  }

  return null;
};

export const getPlatformShiftInMinutes = (
  platformTime: DateTimeInput,
): number | null => {
  const nowByPlatform = parseDateTime(platformTime);
  if (!nowByPlatform) {
    return null;
  }

  const nowByUser = dayjs();
  // Bias toward a later scheduled time to avoid sending one minute earlier.
  return Math.ceil(nowByPlatform.diff(nowByUser, 'minute', true));
};

export const convertUserDateTimeToPlatform = ({
  dateTime,
  platformTime,
}: ConvertDateTimeParams): Dayjs | null => {
  const value = parseDateTime(dateTime);
  const shift = getPlatformShiftInMinutes(platformTime);

  if (!value || shift === null) {
    return null;
  }

  return value.add(shift, 'minute');
};

export const convertPlatformDateTimeToUser = ({
  dateTime,
  platformTime,
}: ConvertDateTimeParams): Dayjs | null => {
  const value = parseDateTime(dateTime);
  const shift = getPlatformShiftInMinutes(platformTime);

  if (!value || shift === null) {
    return null;
  }

  return value.subtract(shift, 'minute');
};
